// src/Registration.js
import React, {useState} from 'react';
import {Box, Button, Card, Container, Grid, IconButton, InputAdornment, TextField, Typography} from '@mui/material';
import {useNavigate} from "react-router-dom";
import axios from "axios";
import Logo from "../core/components/Logo";
import {ThemedText} from "../core/components/ThemedText";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const Register = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords don't match");
      return;
    }

    try {
      // Create a checkout session on the server
      const client = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
      });

      const { data, status } = await client.post('/auth/register', formData);

      if (!status === 200) {
        throw new Error('Network response was not ok');
      }
      // Redirect to the checkout page
      navigate('/pricing', { state: { user: data } });
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  return (
      <Container maxWidth="xs">
        <Box
            sx={{
              marginTop: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
        >
          <Card sx={{ p: 3, width: '100%' }}>
            <Logo />
            <ThemedText align={'center'} style={{marginTop: '20px', marginBottom: '20px'}} type={'title'}>Create your account</ThemedText>
            <ThemedText style={{marginBottom: '25px'}} align="center" type={'subtitle'}>
              Welcome! Please fill in the details to get started.
            </ThemedText>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <Grid container spacing={2} style={{marginBottom: '15px'}}>
                <Grid item xs={12} sm={6}>
                  <TextField
                      name="firstName"
                      required
                      fullWidth
                      id="firstName"
                      label="First Name"
                      autoFocus
                      value={formData.firstName}
                      onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                      name="lastName"
                      required
                      fullWidth
                      id="lastName"
                      label="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                      name="company"
                      fullWidth
                      id="company"
                      label="Company"
                      value={formData.company}
                      onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                      name="email"
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                      name="phone"
                      fullWidth
                      id="phone"
                      label="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                      name="password"
                      required
                      fullWidth
                      id="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                                  onClick={() => setShowPassword((prev) => !prev)}
                                  edge="end"
                                  tabIndex={-1}
                              >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                        ),
                      }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                      name="confirmPassword"
                      required
                      fullWidth
                      id="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                                  edge="end"
                                  tabIndex={-1}
                              >
                                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                        ),
                      }}
                  />
                  {passwordError && (
                      <Typography color="error" variant="body2">
                        {passwordError}
                      </Typography>
                  )}
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                >
                  Register
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>
  );
};

export default Register;
